import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { Location } from 'react-router-dom';

import { useAuth } from '../../../context/AuthContext';
import { useBilling } from '../../../context/BillingContext';
import { requestPasswordReset, confirmPasswordReset } from '../../../api/authApi';
import ErrorBanner from '../../../components/ErrorBanner';
import { Captcha, loadConfig } from '../../../components/Captcha';
import { LegalModal, type LegalDocumentType } from '../../../components/LegalModal';
import { Eyebrow, PageHeading, InlineAlert } from '../../../design-system';
import styles from './AuthPortalPage.module.css';
import theme from '../../marketing/theme.module.css';

interface AuthPortalPageProps {
  initialMode?: 'login' | 'register';
}

type LocationState = {
  state?: {
    from?: Location;
  };
};

const USER_REMEMBER_KEY = 'policyora_remembered_email';
const USER_REMEMBER_LIST_KEY = 'policyora_remembered_emails';
const MAX_REMEMBERED_EMAILS = 5;

/**
 * Visual-only password strength score (0–4) used to drive the 3-segment
 * meter under the signup password input. Does NOT gate submission; the
 * existing validation rules (8+ chars, 1 letter + 1 number, max 72) still
 * govern whether the form is submittable.
 *
 * Scoring:
 *   0 — empty
 *   1 — weak  (any input ≥1 char)
 *   2 — fair  (≥8 chars with at least 2 of {lower, upper, digit, symbol})
 *   3 — good  (≥10 chars with 3 of those classes)
 *   4 — strong (≥12 chars with all 4 classes)
 */
const passwordStrengthScore = (pw: string): 0 | 1 | 2 | 3 | 4 => {
  if (!pw) return 0;
  const classes =
    Number(/[a-z]/.test(pw)) +
    Number(/[A-Z]/.test(pw)) +
    Number(/\d/.test(pw)) +
    Number(/[^A-Za-z0-9]/.test(pw));
  if (pw.length >= 12 && classes >= 4) return 4;
  if (pw.length >= 10 && classes >= 3) return 3;
  if (pw.length >= 8 && classes >= 2) return 2;
  return 1;
};

const passwordStrengthLabel = (score: number): string =>
  ['', 'Weak', 'Fair', 'Good', 'Strong'][score] ?? '';

const getRememberedValue = (key: string): string => {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
};

const getRememberedValues = (key: string): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    return [];
  }
};

const setRememberedValue = (key: string, value: string | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.localStorage.setItem(key, value);
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // ignore storage failures
  }
};

const setRememberedValues = (key: string, values: string[]) => {
  if (typeof window === 'undefined') return;
  try {
    if (values.length > 0) {
      window.localStorage.setItem(key, JSON.stringify(values));
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // ignore storage failures
  }
};

const addRememberedEmail = (email: string): string[] => {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return getRememberedValues(USER_REMEMBER_LIST_KEY);
  const next = [
    normalized,
    ...getRememberedValues(USER_REMEMBER_LIST_KEY).filter((value) => value.toLowerCase() !== normalized),
  ].slice(0, MAX_REMEMBERED_EMAILS);
  setRememberedValues(USER_REMEMBER_LIST_KEY, next);
  setRememberedValue(USER_REMEMBER_KEY, next[0] ?? null);
  return next;
};

const removeRememberedEmail = (email: string): string[] => {
  const normalized = email.trim().toLowerCase();
  const next = getRememberedValues(USER_REMEMBER_LIST_KEY).filter((value) => value.toLowerCase() !== normalized);
  setRememberedValues(USER_REMEMBER_LIST_KEY, next);
  setRememberedValue(USER_REMEMBER_KEY, next[0] ?? null);
  return next;
};

const savePasswordToBrowser = async (
  form: HTMLFormElement | null,
  email: string,
  password: string,
) => {
  if (typeof window === 'undefined' || !('credentials' in navigator)) return;

  try {
    const PasswordCredentialCtor = (window as Window & {
      PasswordCredential?: new (data: HTMLFormElement | { id: string; password: string; name?: string }) => Credential;
    }).PasswordCredential;

    if (!PasswordCredentialCtor || typeof navigator.credentials?.store !== 'function') {
      return;
    }

    const credential = form
      ? new PasswordCredentialCtor(form)
      : new PasswordCredentialCtor({ id: email, password, name: email });

    await navigator.credentials.store(credential);
  } catch {
    // Browser-managed password saving is best-effort only.
  }
};

export const AuthPortalPage: React.FC<AuthPortalPageProps> = ({ initialMode = 'login' }) => {
  const { login, signup, verifyLoginMfa } = useAuth();
  const { ensureTrial } = useBilling();
  const navigate = useNavigate();
  const location = useLocation() as Location & LocationState;
  const [searchParams] = useSearchParams();
  const derivedMode: 'login' | 'register' = location.pathname === '/signup' ? 'register' : initialMode;
  const [mode, setMode] = useState<'login' | 'register'>(derivedMode);
  const loginFormRef = React.useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    setMode(derivedMode);
  }, [derivedMode]);

  const [loginEmail, setLoginEmail] = useState(() => getRememberedValue(USER_REMEMBER_KEY));
  const [rememberedEmails, setRememberedEmails] = useState<string[]>(() => getRememberedValues(USER_REMEMBER_LIST_KEY));
  const [loginPassword, setLoginPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => Boolean(getRememberedValue(USER_REMEMBER_KEY)));
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginEmailError, setLoginEmailError] = useState('');
  const [loginPasswordError, setLoginPasswordError] = useState('');
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [loginInfoMessage, setLoginInfoMessage] = useState<string | null>(null);
  const [loginPasswordVisible, setLoginPasswordVisible] = useState(false);
  // Login OTP (SRS #113): with ENABLE_LOGIN_MFA on, a correct password returns a
  // challenge and the code arrives by email; this page collects it. The step
  // used to exist only in LoginPage, which no route renders — so on live the
  // server sent the code and the form silently reset.
  const [mfaEmail, setMfaEmail] = useState<string | null>(null);
  const [mfaOtp, setMfaOtp] = useState('');
  const [mfaSubmitting, setMfaSubmitting] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [signupCaptcha, setSignupCaptcha] = useState<string | null>(null);
  const [signupCaptchaReset, setSignupCaptchaReset] = useState(0);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [signupEmailError, setSignupEmailError] = useState('');
  const [signupPasswordError, setSignupPasswordError] = useState('');
  const [signupSubmitting, setSignupSubmitting] = useState(false);
  const [signupPasswordVisible, setSignupPasswordVisible] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalDocumentType, setLegalDocumentType] = useState<LegalDocumentType>('terms');

  const [showResetPanel, setShowResetPanel] = useState(false);
  const [resetStep, setResetStep] = useState<'request' | 'confirm'>('request');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCaptcha, setResetCaptcha] = useState<string | null>(null);
  const [resetCaptchaReset, setResetCaptchaReset] = useState(0);
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);

  // If user is logging in from /login, /, or /app (not redirected), go to /dashboard after login.
  // Trial/billing enforcement still happens in ProtectedRoute (expired trials are routed to /billing).
  let from = location.state?.from?.pathname;
  if (!from || from === '/' || from === '/app' || location.pathname === '/login') {
    from = '/dashboard';
  }

  // Persist the ?plan= query param from the pricing page CTA through the signup flow
  useEffect(() => {
    const plan = searchParams.get('plan');
    if (plan && mode === 'register') {
      sessionStorage.setItem('pending_plan_tier', plan);
    }
  }, [searchParams, mode]);

  const switchToLogin = (replace = false) => {
    setMode('login');
    navigate('/login', { replace });
  };

  const switchToRegister = (replace = false) => {
    setMode('register');
    navigate('/signup', { replace });
  };

  const handleLoginSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoginError(null);
    setLoginEmailError('');
    setLoginPasswordError('');

    const nextEmail = loginEmail.trim();
    const nextPassword = loginPassword;

    let hasError = false;

    if (!nextEmail) {
      setLoginEmailError('Email address is required');
      hasError = true;
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(nextEmail)) {
      setLoginEmailError('Enter a valid email address');
      hasError = true;
    }

    if (!nextPassword) {
      setLoginPasswordError('Password is required');
      hasError = true;
    }

    if (hasError) {
      return;
    }

    setLoginSubmitting(true);
    try {
      setLoginInfoMessage(null);
      const result = await login({ email: nextEmail, password: nextPassword });
      if (result?.mfaRequired) {
        // Password accepted — the server emailed a code; collect it before finishing.
        setMfaEmail(result.email ?? nextEmail);
        setMfaOtp('');
        setMfaError(null);
        return;
      }
      await finishLogin(nextEmail, nextPassword);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setLoginError(message);
    } finally {
      setLoginSubmitting(false);
    }
  };

  /** Everything that happens once a session exists — shared by the password
   *  path and the login-code path so the two can never drift. */
  const finishLogin = async (nextEmail: string, nextPassword: string) => {
    if (rememberMe) {
      setRememberedValue(USER_REMEMBER_KEY, nextEmail);
      setRememberedEmails(addRememberedEmail(nextEmail));
      await savePasswordToBrowser(loginFormRef.current, nextEmail, nextPassword);
    } else {
      setRememberedEmails(removeRememberedEmail(nextEmail));
    }
    const pendingPlan = sessionStorage.getItem('pending_plan_tier');
    if (pendingPlan) {
      // User came from pricing page CTA — honour their chosen plan
      sessionStorage.removeItem('pending_plan_tier');
      navigate(`/billing/checkout?plan=${pendingPlan}`, { replace: true });
    } else {
      // ensureTrial only fires when a plan was explicitly stored (e.g. from PricingPage)
      await ensureTrial();
      navigate(from, { replace: true });
    }
  };

  const handleMfaSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!mfaEmail || mfaOtp.length < 6) return;
    setMfaSubmitting(true);
    setMfaError(null);
    try {
      await verifyLoginMfa(mfaEmail, mfaOtp);
      await finishLogin(mfaEmail, loginPassword);
    } catch (err) {
      setMfaError(err instanceof Error ? err.message : 'That code did not work. Please try again.');
    } finally {
      setMfaSubmitting(false);
    }
  };

  const handleSignupSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSignupError(null);
    setFirstNameError('');
    setLastNameError('');
    setSignupEmailError('');
    setSignupPasswordError('');

    const nextFirst = firstName.trim();
    const nextLast = lastName.trim();
    const nextName = `${nextFirst} ${nextLast}`.trim();   // backend still receives `full_name`
    const nextEmail = signupEmail.trim();
    const nextPassword = signupPassword;

    let hasError = false;

    if (!nextFirst) {
      setFirstNameError('First name is required');
      hasError = true;
    }
    if (!nextLast) {
      setLastNameError('Last name is required');
      hasError = true;
    }

    if (!nextEmail) {
      setSignupEmailError('Email address is required');
      hasError = true;
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(nextEmail)) {
      setSignupEmailError('Enter a valid email address');
      hasError = true;
    }

    if (!nextPassword) {
      setSignupPasswordError('Password is required');
      hasError = true;
    } else if (nextPassword.length < 8) {
      setSignupPasswordError('Password must be at least 8 characters long');
      hasError = true;
    } else if (!/[A-Za-z]/.test(nextPassword) || !/\d/.test(nextPassword)) {
      setSignupPasswordError('Include at least one letter and one number');
      hasError = true;
    } else if (nextPassword.length > 72) {
      setSignupPasswordError('Password must be at most 72 characters long');
      hasError = true;
    }

    if (!agreeToTerms) {
      setSignupError('Please review and accept the Terms & Conditions');
      hasError = true;
    }

    if (hasError) {
      return;
    }

    setSignupSubmitting(true);
    try {
      const result = await signup({ full_name: nextName, email: nextEmail, password: nextPassword, captcha_token: signupCaptcha });
      navigate('/verify-email', { replace: true, state: { email: result.email, redirectTo: from } });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Signup failed';
      setSignupError(message);
      setSignupCaptchaReset((n) => n + 1); // a Turnstile token is single-use
    } finally {
      setSignupSubmitting(false);
    }
  };

  const openResetPanel = () => {
    setShowResetPanel(true);
    setResetStep('request');
    setResetMessage(null);
    setResetError(null);
    setResetSubmitting(false);
    if (!resetEmail) {
      setResetEmail(loginEmail || signupEmail);
    }
  };

  const closeResetPanel = () => {
    setShowResetPanel(false);
    setResetStep('request');
    setResetCode('');
    setResetNewPassword('');
    setResetSubmitting(false);
    setResetError(null);
    setResetMessage(null);
  };

  const openLegalModal = (documentType: LegalDocumentType) => {
    setLegalDocumentType(documentType);
    setLegalModalOpen(true);
  };

  const handleLegalAccept = () => {
    setAgreeToTerms(true);
    setSignupError(null);
    setLegalModalOpen(false);
  };

  const handleResetRequestSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const email = resetEmail.trim();
    if (!email) {
      setResetError('Enter the account email first');
      return;
    }
    setResetSubmitting(true);
    setResetError(null);
    setResetMessage(null);
    try {
      await requestPasswordReset(email, resetCaptcha);
      setResetMessage('Check your inbox for the verification code.');
      setResetStep('confirm');
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Failed to start reset');
      setResetCaptchaReset((n) => n + 1);
    } finally {
      setResetSubmitting(false);
    }
  };

  const handleResetConfirmSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const email = resetEmail.trim();
    if (!email || !resetCode.trim() || !resetNewPassword) {
      setResetError('Please fill in all fields');
      return;
    }
    setResetSubmitting(true);
    setResetError(null);
    setResetMessage(null);
    try {
      await confirmPasswordReset({ email, code: resetCode.trim(), new_password: resetNewPassword });
      setResetMessage('Password updated. You can now log in with the new password.');
      setLoginEmail(email);
      setLoginInfoMessage('Password reset successful. Please log in with your new password.');
      setResetStep('request');
      setResetCode('');
      setResetNewPassword('');
      setShowResetPanel(false);
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Failed to confirm reset');
    } finally {
      setResetSubmitting(false);
    }
  };

  const googleHref = `${process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000'}/api/v1/auth/google`;
  // Only offer Google when the server has an OAuth client configured — a dead button on a sign-in
  // form is worse than none (the client id is still pending from the customer).
  const [googleEnabled, setGoogleEnabled] = useState(false);
  useEffect(() => { loadConfig().then((c) => setGoogleEnabled(Boolean(c.google_sign_in))).catch(() => setGoogleEnabled(false)); }, []);

  const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="var(--ds-brand-blue-light)" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="var(--ds-success)" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="var(--ds-brand-blue)" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="var(--ds-danger)" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );

  const EyeIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );

  const EyeOffIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 3l18 18" /><path d="M9.88 9.88A3 3 0 0 0 12 15a3 3 0 0 0 2.12-5.12" />
      <path d="M10.73 5.08A10.21 10.21 0 0 1 12 5c5 0 9.27 3.11 11 7.5a13.16 13.16 0 0 1-2.17 3.18" />
      <path d="M6.61 6.61C4.07 8.07 2.24 10.43 1 12.5c1.73 4.39 6 7.5 11 7.5 1.27 0 2.5-.19 3.64-.54" />
    </svg>
  );

  // --- Trust badge icons (SOC 2 / E2E / GDPR row at the bottom) ----------
  const CheckBadgeIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 12l2 2 4-4" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
  const LockBadgeIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="11" width="16" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 018 0v3" />
    </svg>
  );
  const ShieldBadgeIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l8 3v5c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-3z" />
    </svg>
  );

  return (
    <div className={`${theme.theme} ${styles.root}`}>
      <div className={styles.themeToggleContainer}>
      </div>

      {/* Left branding panel */}
      <div className={styles.brandPanel}>
        {/* Logo is a direct child of the panel (NOT inside .brandContent)
            so it can center within the full panel width. .brandContent
            is constrained to max-width:460px to keep the body copy at a
            readable measure, but that constraint would visually pin the
            logo to the left if it lived inside that column. */}
        <img
          className={styles.brandLogoImage}
          src="/home2-logo.png"
          alt="PolicyOra"
          draggable={false}
        />

        <div className={styles.brandContent}>
          <div className={styles.brandEyebrow}>
            <span className={styles.brandEyebrowDot} aria-hidden="true" />
            GLOBAL POLICY INTELLIGENCE
          </div>

          <h1 className={styles.brandTagline}>
            Stay ahead of<br />every <span className={styles.brandTaglineAccent}>regulation</span>
          </h1>

          <p className={styles.brandDescription}>
            AI-powered policy analysis trusted by compliance leaders
            across 60+ jurisdictions worldwide.
          </p>

          <div className={styles.brandSectionTitle}>KEY CAPABILITIES</div>
          <div className={styles.brandFeatures}>
            {[
              {
                title: 'Real-time regulatory updates',
                // Bell with notification dot — signals live/incoming updates.
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                    <circle cx="18" cy="6" r="2.5" fill="currentColor" stroke="none" />
                  </svg>
                ),
              },
              {
                title: 'AI-powered policy analysis',
                // Sparkles — established AI iconography used elsewhere in the app.
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                    <path d="M5 3v4" />
                    <path d="M19 17v4" />
                    <path d="M3 5h4" />
                    <path d="M17 19h4" />
                  </svg>
                ),
              },
              {
                title: 'Team collaboration tools',
                // Users — two-person glyph for collaboration.
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                ),
              },
              {
                title: 'Global jurisdiction coverage',
                // Globe with meridians — represents global reach across jurisdictions.
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                ),
              },
            ].map((f) => (
              <div key={f.title} className={styles.brandFeature}>
                <span className={styles.brandFeatureIcon} aria-hidden="true">
                  {f.icon}
                </span>
                <span className={styles.brandFeatureLabel}>{f.title}</span>
              </div>
            ))}
          </div>

          <div className={styles.brandStats}>
            <div className={styles.brandStat}>
              <div className={styles.brandStatLabel}>ORGANIZATIONS</div>
              <div className={styles.brandStatValue}>1,200+</div>
            </div>
            <div className={styles.brandStat}>
              <div className={styles.brandStatLabel}>AVG COMPLIANCE</div>
              <div className={`${styles.brandStatValue} ${styles.brandStatValueAccent}`}>98.4%</div>
            </div>
            <div className={styles.brandStat}>
              <div className={styles.brandStatLabel}>JURISDICTIONS</div>
              <div className={styles.brandStatValue}>60+</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className={styles.formPanel}>
        {mode === 'login' && mfaEmail ? (
          <form className={styles.formCard} onSubmit={handleMfaSubmit} data-testid="login-mfa-step">
            <div className={styles.formEyebrow}>ONE MORE STEP</div>
            <div>
              <h2 className={styles.formTitle}>Enter your login code</h2>
              <p className={styles.formSubtitle}>We emailed a 6-digit code to <strong>{mfaEmail}</strong>. It is valid for 10 minutes.</p>
            </div>
            {mfaError && <ErrorBanner variant="inline" align="center" message={mfaError} />}
            <div className={styles.inputGroup}>
              <label htmlFor="login-otp">LOGIN CODE</label>
              <input
                id="login-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                value={mfaOtp}
                onChange={(e) => setMfaOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                autoFocus
                style={{ letterSpacing: 6, textAlign: 'center', fontSize: 20 }}
              />
            </div>
            <button className={styles.submitButton} type="submit" disabled={mfaSubmitting || mfaOtp.length < 6}>
              {mfaSubmitting ? 'Verifying…' : 'Verify & sign in'}
            </button>
            <p className={styles.switchCopy}>
              Didn't get it? Check spam, or{' '}
              <button type="button" className={styles.helperLinkButton} onClick={() => { setMfaEmail(null); setMfaOtp(''); setMfaError(null); }}>
                go back and sign in again
              </button>
              {' '}to receive a new code.
            </p>
          </form>
        ) : mode === 'login' ? (
          <form ref={loginFormRef} className={styles.formCard} onSubmit={handleLoginSubmit} autoComplete="on">
            <div className={styles.formEyebrow}>WELCOME BACK</div>
            <div>
              <h2 className={styles.formTitle}>Sign in to PolicyOra</h2>
              <p className={styles.formSubtitle}>Continue where you left off.</p>
            </div>

            {loginError && (
              <ErrorBanner
                variant="inline"
                align="center"
                message={loginError}
                details={
                  loginError.toLowerCase().includes('not verified')
                    ? undefined
                    : 'Please verify your credentials and try again.'
                }
              >
                {loginError.toLowerCase().includes('not verified') && (
                  <div style={{ marginTop: '8px' }}>
                    <button
                      type="button"
                      className={styles.helperLinkButton}
                      onClick={() => navigate('/verify-email', { state: { email: loginEmail.trim(), redirectTo: from } })}
                    >
                      Go to email verification →
                    </button>
                  </div>
                )}
              </ErrorBanner>
            )}
            {loginInfoMessage && <div className={styles.info}>{loginInfoMessage}</div>}

            <div className={styles.inputGroup}>
              <label htmlFor="login-email">WORK EMAIL</label>
              <input
                id="login-email"
                name="email"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                spellCheck={false}
                list="remembered-login-emails"
                required
              />
              {rememberedEmails.length > 0 && (
                <>
                  <datalist id="remembered-login-emails">
                    {rememberedEmails.map((email) => <option key={email} value={email} />)}
                  </datalist>
                  <div className={styles.emailPills}>
                    {rememberedEmails.map((email) => (
                      <button
                        key={email}
                        type="button"
                        className={styles.emailPill}
                        onClick={() => {
                          setLoginEmail(email);
                          setRememberMe(true);
                        }}
                      >
                        {email}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {loginEmailError && <span className={styles.fieldError}>{loginEmailError}</span>}
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="login-password">Password</label>
              <div className={styles.passwordField}>
                <input
                  id="login-password"
                  name="password"
                  type={loginPasswordVisible ? 'text' : 'password'}
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
                <button type="button" className={styles.passwordToggle} onClick={() => setLoginPasswordVisible(p => !p)} aria-label="Toggle password">
                  {loginPasswordVisible ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {loginPasswordError && <span className={styles.fieldError}>{loginPasswordError}</span>}
            </div>

            <div className={styles.inlineRow}>
              <label>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setRememberMe(checked);
                    if (!checked) {
                      setRememberedEmails(removeRememberedEmail(loginEmail || getRememberedValue(USER_REMEMBER_KEY)));
                    }
                  }}
                />
                Remember email on this device
              </label>
              <button type="button" className={styles.helperLinkButton} onClick={openResetPanel}>
                Forgot password?
              </button>
            </div>
            <p className={styles.passwordHint}>
              Password saving is handled securely by your browser when supported.
            </p>

            <button className={styles.submitButton} type="submit" disabled={loginSubmitting}>
              {loginSubmitting ? 'Signing in…' : 'Sign in'}
            </button>

            {googleEnabled && (<>
              <div className={styles.divider}><span>OR CONTINUE WITH</span></div>
              <a className={styles.googleButton} href={googleHref}>
                <GoogleIcon />
                Continue with Google
              </a>
            </>)}

            <p className={styles.switchCopy}>
              New to PolicyOra?{' '}
              <button type="button" className={styles.helperLinkButton} onClick={() => switchToRegister(true)}>
                Create account →
              </button>
            </p>

            {/* Trust badges — confirmed accurate today */}
            <div className={styles.trustBadges}>
              <span className={styles.trustBadge}><CheckBadgeIcon /> SOC 2 Certified</span>
              <span className={styles.trustBadge}><LockBadgeIcon /> End-to-end encrypted</span>
              <span className={styles.trustBadge}><ShieldBadgeIcon /> GDPR Compliant</span>
            </div>
          </form>
        ) : (
          <form className={styles.formCard} onSubmit={handleSignupSubmit}>
            <div className={styles.formEyebrow}>GET STARTED — IT'S FREE</div>
            <div>
              <h2 className={styles.formTitle}>Create your account</h2>
              <p className={styles.formSubtitle}>Join PolicyOra and stay ahead of every regulation.</p>
            </div>

            {signupError && (
              <ErrorBanner
                variant="inline"
                align="center"
                message={signupError}
                details="Please review the form and try again."
              />
            )}

            {/* First + Last name side by side */}
            <div className={styles.nameRow}>
              <div className={styles.inputGroup}>
                <label htmlFor="signup-first-name">FIRST NAME</label>
                <input
                  id="signup-first-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Alex"
                  autoComplete="given-name"
                  required
                />
                {firstNameError && <span className={styles.fieldError}>{firstNameError}</span>}
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="signup-last-name">LAST NAME</label>
                <input
                  id="signup-last-name"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Kim"
                  autoComplete="family-name"
                  required
                />
                {lastNameError && <span className={styles.fieldError}>{lastNameError}</span>}
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="signup-email">WORK EMAIL</label>
              <input
                id="signup-email"
                type="email"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                required
              />
              {signupEmailError && <span className={styles.fieldError}>{signupEmailError}</span>}
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="signup-password">PASSWORD</label>
              <div className={styles.passwordField}>
                <input
                  id="signup-password"
                  type={signupPasswordVisible ? 'text' : 'password'}
                  value={signupPassword}
                  onChange={(e) => setSignupPassword(e.target.value)}
                  placeholder="Create a strong password"
                  autoComplete="new-password"
                  required
                />
                <button type="button" className={styles.passwordToggle} onClick={() => setSignupPasswordVisible(p => !p)} aria-label="Toggle password">
                  {signupPasswordVisible ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>

              {/* Strength meter — visual only, doesn't gate submission */}
              {signupPassword && (() => {
                const score = passwordStrengthScore(signupPassword);
                return (
                  <div
                    className={styles.strengthMeter}
                    data-score={score}
                    aria-label={`Password strength: ${passwordStrengthLabel(score)}`}
                  >
                    <span className={styles.strengthBar} />
                    <span className={styles.strengthBar} />
                    <span className={styles.strengthBar} />
                    <span className={styles.strengthBar} />
                    <span className={styles.strengthLabel}>{passwordStrengthLabel(score)}</span>
                  </div>
                );
              })()}

              {signupPasswordError && <span className={styles.fieldError}>{signupPasswordError}</span>}
            </div>

            <label className={styles.checkboxRow}>
              <input type="checkbox" checked={agreeToTerms} onChange={(e) => setAgreeToTerms(e.target.checked)} />
              <span>
                I agree to the{' '}
                <button type="button" className={styles.helperLinkButton} onClick={() => openLegalModal('terms')}>
                  Terms &amp; Conditions
                </button>
                {' '}and{' '}
                <button type="button" className={styles.helperLinkButton} onClick={() => openLegalModal('privacy')}>
                  Privacy Policy
                </button>
                . PolicyOra is committed to your data security.
              </span>
            </label>

            <Captcha onToken={setSignupCaptcha} resetKey={signupCaptchaReset} />

            <button className={styles.submitButton} type="submit" disabled={signupSubmitting}>
              {signupSubmitting ? 'Creating account…' : 'Create my account'}
            </button>

            {googleEnabled && (<>
              <div className={styles.divider}><span>OR CONTINUE WITH</span></div>
              <a className={styles.googleButton} href={googleHref}>
                <GoogleIcon />
                Continue with Google
              </a>
            </>)}

            <p className={styles.switchCopy}>
              Already have an account?{' '}
              <button type="button" className={styles.helperLinkButton} onClick={() => switchToLogin(true)}>
                Sign in →
              </button>
            </p>

            {/* Trust badges — confirmed accurate today */}
            <div className={styles.trustBadges}>
              <span className={styles.trustBadge}><CheckBadgeIcon /> SOC 2 Certified</span>
              <span className={styles.trustBadge}><LockBadgeIcon /> End-to-end encrypted</span>
              <span className={styles.trustBadge}><ShieldBadgeIcon /> GDPR Compliant</span>
            </div>
          </form>
        )}
      </div>

      <LegalModal
        open={legalModalOpen}
        documentType={legalDocumentType}
        onSelectDocument={setLegalDocumentType}
        onClose={() => setLegalModalOpen(false)}
        showAcceptButton={mode === 'register'}
        onAccept={handleLegalAccept}
      />

      {showResetPanel && (
        <div className={styles.resetBackdrop}>
          <div className={styles.resetCard} role="dialog" aria-modal="true">
            <div className={styles.resetHeader}>
              <div>
                <Eyebrow tone="default" dot>
                  {resetStep === 'request' ? 'PASSWORD RESET' : 'VERIFICATION'}
                </Eyebrow>
                <PageHeading
                  accentWord={resetStep === 'request' ? 'password' : 'code'}
                  accentTone="blue"
                  size="h3"
                  align="left"
                  level="h3"
                >
                  {resetStep === 'request' ? 'Reset your password' : 'Enter verification code'}
                </PageHeading>
              </div>
              <button type="button" className={styles.resetCloseButton} onClick={closeResetPanel} aria-label="Close">
                ×
              </button>
            </div>
            {resetMessage && (
              <InlineAlert variant="success" title={resetMessage} />
            )}
            {resetError && (
              <InlineAlert variant="danger" title={resetError} />
            )}
            <form onSubmit={resetStep === 'request' ? handleResetRequestSubmit : handleResetConfirmSubmit}>
              <label className={styles.resetLabel}>
                Email
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(event) => setResetEmail(event.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </label>
              {resetStep === 'confirm' && (
                <>
                  <label className={styles.resetLabel}>
                    Verification code
                    <input
                      type="text"
                      value={resetCode}
                      onChange={(event) => setResetCode(event.target.value)}
                      placeholder="Enter the 6-digit code"
                      required
                    />
                  </label>
                  <label className={styles.resetLabel}>
                    New password
                    <div className={styles.passwordField}>
                      <input
                        type={resetPasswordVisible ? 'text' : 'password'}
                        value={resetNewPassword}
                        onChange={(event) => setResetNewPassword(event.target.value)}
                        placeholder="Create a strong password"
                        required
                      />
                      <button
                        type="button"
                        className={styles.passwordToggle}
                        onClick={() => setResetPasswordVisible(prev => !prev)}
                        aria-label={resetPasswordVisible ? 'Hide password' : 'Show password'}
                      >
                        {resetPasswordVisible ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M3 3l18 18" />
                            <path d="M9.88 9.88A3 3 0 0 0 12 15a3 3 0 0 0 2.12-5.12" />
                            <path d="M10.73 5.08A10.21 10.21 0 0 1 12 5c5 0 9.27 3.11 11 7.5a13.16 13.16 0 0 1-2.17 3.18" />
                            <path d="M6.61 6.61C4.07 8.07 2.24 10.43 1 12.5c1.73 4.39 6 7.5 11 7.5 1.27 0 2.5-.19 3.64-.54" />
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </label>
                </>
              )}
              {resetStep === 'request' && (
                <Captcha onToken={setResetCaptcha} resetKey={resetCaptchaReset} />
              )}
              <div className={styles.resetActions}>
                {resetStep === 'confirm' && (
                  <button
                    type="button"
                    className={styles.helperLinkButton}
                    onClick={() => {
                      setResetStep('request');
                      setResetCode('');
                      setResetNewPassword('');
                      setResetMessage(null);
                    }}
                  >
                    Start over
                  </button>
                )}
                <button className={styles.submitButton} type="submit" disabled={resetSubmitting}>
                  {resetSubmitting
                    ? 'Please wait…'
                    : resetStep === 'request'
                      ? 'Send reset link'
                      : 'Update password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
